import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, ExternalLink, Clock, Calendar, ChevronRight, 
  CheckCircle, ImageOff, ArrowRight, Tag 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { SEO } from '../components/SEO';
import { BeforeAfter } from '../components/BeforeAfter';
import projects from '../data/projects';

function ScreenshotPlaceholder({ label }) {
  return (
    <div className="aspect-video rounded-2xl bg-muted/50 border-2 border-dashed border-border flex flex-col items-center justify-center gap-3">
      <ImageOff className="h-10 w-10 text-muted-foreground/30" />
      <span className="text-sm text-muted-foreground/50 font-medium">{label || 'Screenshot à venir'}</span>
    </div>
  );
}

function PhoneMockup({ screenshot, title, color }) {
  return (
    <div className="relative mx-auto" style={{ width: '260px' }}>
      {/* Phone frame */}
      <div className="relative rounded-[2.5rem] border-[6px] border-zinc-800 dark:border-zinc-600 bg-black overflow-hidden shadow-2xl">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-black rounded-b-2xl z-20" />
        {/* Screen */}
        <div className="relative aspect-[9/19.5] overflow-hidden bg-zinc-900">
          {screenshot ? (
            <img
              src={screenshot}
              alt={title}
              className="w-full h-full object-cover object-top"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageOff className="h-8 w-8 text-zinc-600" />
            </div>
          )}
        </div>
      </div>
      {/* Glow */}
      <div 
        className="absolute -inset-4 rounded-[3rem] opacity-15 blur-2xl -z-10"
        style={{ background: color }}
      />
    </div>
  );
}

function StoreButtons({ appStore, playStore }) {
  return (
    <>
      {appStore && (
        <a href={appStore} target="_blank" rel="noopener noreferrer" data-testid="appstore-btn">
          <div className="flex items-center gap-2.5 px-5 py-2.5 bg-black text-white rounded-xl hover:bg-zinc-800 transition-colors border border-zinc-700">
            <svg viewBox="0 0 24 24" className="h-6 w-6 fill-current">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            <div className="leading-tight">
              <div className="text-[10px] opacity-70">Disponible sur</div>
              <div className="text-sm font-semibold -mt-0.5">App Store</div>
            </div>
          </div>
        </a>
      )}
      {playStore && (
        <a href={playStore} target="_blank" rel="noopener noreferrer" data-testid="playstore-btn">
          <div className="flex items-center gap-2.5 px-5 py-2.5 bg-black text-white rounded-xl hover:bg-zinc-800 transition-colors border border-zinc-700">
            <svg viewBox="0 0 24 24" className="h-6 w-6 fill-current">
              <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.49 1.441c.65.376.65 1.326 0 1.702l-2.49 1.441-2.534-2.534 2.534-2.05zM5.864 2.658L16.8 9.49l-2.302 2.302-8.634-8.634z"/>
            </svg>
            <div className="leading-tight">
              <div className="text-[10px] opacity-70">Disponible sur</div>
              <div className="text-sm font-semibold -mt-0.5">Google Play</div>
            </div>
          </div>
        </a>
      )}
    </>
  );
}

function MobileAppHero({ project }) {
  return (
    <section className="relative pt-32 pb-20 overflow-hidden">
      <div 
        className="absolute inset-0 opacity-[0.04]"
        style={{ background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${project.color}, transparent)` }}
      />
      <div className="absolute inset-0 noise pointer-events-none" />

      <div className="container-main relative z-10">
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-10 animate-fade-in" data-testid="breadcrumb">
          <Link to="/realisations" className="hover:text-foreground transition-colors">Réalisations</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-foreground font-medium">{project.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Info */}
          <div className="animate-fade-up">
            <Badge 
              variant="outline" 
              className="mb-6 text-sm font-semibold px-4 py-1.5"
              style={{ borderColor: `${project.color}40`, color: project.color }}
            >
              {project.type}
            </Badge>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-4">
              {project.name}
            </h1>
            
            <p className="text-base md:text-lg text-muted-foreground mb-6">{project.tagline}</p>

            <p className="text-muted-foreground leading-relaxed mb-8">
              {project.description}
            </p>

            <div className="flex flex-wrap items-center gap-4 mb-8 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>{project.duration}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>{project.year}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {project.badges.map((badge) => {
                const Icon = badge.icon;
                return (
                  <span
                    key={badge.label}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-muted border border-border text-foreground"
                  >
                    <Icon className="h-3.5 w-3.5 text-primary" />
                    {badge.label}
                  </span>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <StoreButtons appStore={project.appStore} playStore={project.playStore} />
              <a href="/tarifs#contact">
                <Button variant="outline" className="rounded-full px-6 py-5 font-bold" data-testid="contact-btn">
                  Me contacter
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>

          {/* Right: App icon large */}
          <div className="animate-fade-up flex justify-center" style={{ animationDelay: '0.2s' }}>
            <div className="relative">
              <img
                src={project.logo}
                alt={`Logo ${project.name}`}
                className="w-48 h-48 sm:w-56 sm:h-56 lg:w-64 lg:h-64 object-cover rounded-[22%] shadow-2xl"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div 
                className="absolute -inset-6 rounded-[28%] opacity-20 blur-3xl -z-10"
                style={{ background: project.color }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MobileAppFeatures({ project }) {
  if (!project.features || project.features.length === 0) return null;
  
  return (
    <section data-testid="features-section">
      {project.features.map((feature, idx) => {
        const Icon = feature.icon;
        const isEven = idx % 2 === 0;
        return (
          <div 
            key={idx}
            className={`section-padding ${idx % 2 !== 0 ? 'bg-muted/20' : ''}`}
          >
            <div className="container-main">
              <div className={`grid lg:grid-cols-[1fr_auto] gap-12 lg:gap-20 items-center ${!isEven ? 'lg:[direction:rtl]' : ''}`}>
                {/* Text */}
                <div className={`space-y-6 animate-fade-up ${!isEven ? 'lg:[direction:ltr]' : ''}`}>
                  <div className="inline-flex items-center gap-3">
                    <div className="p-3 rounded-xl" style={{ background: `${project.color}15` }}>
                      <Icon className="h-6 w-6" style={{ color: project.color }} />
                    </div>
                    <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold tracking-tight">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-base md:text-lg">
                    {feature.description}
                  </p>
                </div>

                {/* Phone mockup */}
                <div className={`animate-fade-up ${!isEven ? 'lg:[direction:ltr]' : ''}`} style={{ animationDelay: '0.15s' }}>
                  <PhoneMockup 
                    screenshot={feature.screenshot}
                    title={feature.title}
                    color={project.color}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
}

function WebsiteHero({ project }) {
  const isForSale = !!project.forSale;
  const useCover = project.logoStyle === 'cover';
  const heroImage = (isForSale || useCover) && project.features && project.features[0]?.screenshot
    ? project.features[0].screenshot
    : null;

  return (
    <section className="relative pt-32 pb-20 overflow-hidden">
      <div 
        className="absolute inset-0 opacity-[0.04]"
        style={{ background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${project.color}, transparent)` }}
      />
      <div className="absolute inset-0 noise pointer-events-none" />

      <div className="container-main relative z-10">
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-10 animate-fade-in" data-testid="breadcrumb">
          <Link to="/realisations" className="hover:text-foreground transition-colors">Réalisations</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-foreground font-medium">{project.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="animate-fade-up">
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <Badge 
                variant="outline" 
                className="text-sm font-semibold px-4 py-1.5"
                style={{ borderColor: `${project.color}40`, color: project.color }}
              >
                {project.type}
              </Badge>
              {isForSale && (
                <Badge 
                  className="text-sm font-bold px-4 py-1.5 bg-amber-500 text-zinc-900 hover:bg-amber-500 border-amber-400 flex items-center gap-1.5"
                  data-testid="for-sale-badge"
                >
                  <Tag className="h-3.5 w-3.5" />
                  À vendre
                </Badge>
              )}
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-4">
              {project.name}
            </h1>
            
            <p className="text-base md:text-lg text-muted-foreground mb-6">{project.tagline}</p>

            <p className="text-muted-foreground leading-relaxed mb-8">
              {project.description}
            </p>

            <div className="flex flex-wrap items-center gap-4 mb-8 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>{project.duration}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>{project.year}</span>
              </div>
              {isForSale && project.salePrice && (
                <div className="flex items-center gap-2 text-amber-600 font-semibold">
                  <Tag className="h-4 w-4" />
                  <span>{project.salePrice}</span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {project.badges.map((badge) => {
                const Icon = badge.icon;
                return (
                  <span
                    key={badge.label}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-muted border border-border text-foreground"
                  >
                    <Icon className="h-3.5 w-3.5 text-primary" />
                    {badge.label}
                  </span>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-3">
              {project.url && project.url !== '#' && !project.comingSoon && (
                <a href={project.url} target="_blank" rel="noopener noreferrer">
                  <Button className="btn-primary" data-testid="visit-site-btn">
                    {isForSale ? 'Voir la démo' : 'Voir le site'}
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              )}
              {project.comingSoon && (
                <Button disabled className="rounded-full px-8 py-4 font-bold opacity-50 cursor-not-allowed" data-testid="visit-site-btn-disabled">
                  Site en cours de finalisation
                  <Clock className="ml-2 h-4 w-4" />
                </Button>
              )}
              {isForSale ? (
                <a href="/tarifs#contact">
                  <Button 
                    className="rounded-full px-8 py-4 font-bold bg-amber-500 text-zinc-900 hover:bg-amber-400" 
                    data-testid="acquire-site-btn"
                  >
                    <Tag className="mr-2 h-4 w-4" />
                    Acquérir ce site
                  </Button>
                </a>
              ) : (
                <a href="/tarifs#contact">
                  <Button variant="outline" className="rounded-full px-8 py-4 font-bold" data-testid="contact-btn">
                    Me contacter
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              )}
            </div>
          </div>

          <div className="animate-fade-up flex justify-center" style={{ animationDelay: '0.2s' }}>
            {heroImage ? (
              <div className="relative w-full max-w-md">
                <div 
                  className={`relative rounded-3xl overflow-hidden border-2 shadow-2xl ${isForSale ? 'border-amber-400/40' : 'border-border/50'}`}
                >
                  <img
                    src={heroImage}
                    alt={`Aperçu ${project.name}`}
                    className="w-full h-auto object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  {isForSale && (
                    <div className="absolute top-4 right-4 z-20">
                      <Badge className="bg-amber-500 text-zinc-900 border-amber-400 text-sm font-bold hover:bg-amber-500 shadow-lg flex items-center gap-1.5 px-3 py-1.5">
                        <Tag className="h-3.5 w-3.5" />
                        À vendre
                      </Badge>
                    </div>
                  )}
                </div>
                <div 
                  className="absolute -inset-4 rounded-[2rem] opacity-20 blur-3xl -z-10"
                  style={{ background: project.color }}
                />
              </div>
            ) : (
              <div 
                className={`relative w-full max-w-md rounded-3xl overflow-hidden border-2 ${
                  project.logoBackground === 'dark' ? 'border-zinc-800' : 'border-border/50'
                }`}
                style={
                  project.logoBackground === 'dark'
                    ? { background: '#0a0a0a' }
                    : { background: `linear-gradient(145deg, ${project.color}12, white)` }
                }
              >
                <div 
                  className="absolute -inset-[1px] rounded-3xl opacity-30"
                  style={{ background: `linear-gradient(145deg, ${project.color}15, transparent, ${project.color}08)` }}
                />
                {project.logo ? (
                  <img
                    src={project.logo}
                    alt={`Logo ${project.name}`}
                    className="relative z-10 w-full h-auto object-contain p-14 drop-shadow-xl"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="relative z-10 p-20 flex items-center justify-center">
                    <span className="text-7xl font-black" style={{ color: project.color }}>
                      {project.name.charAt(0)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function WebsiteFeatures({ project }) {
  if (!project.features || project.features.length === 0) return null;

  return (
    <section data-testid="features-section">
      {project.features.map((feature, idx) => {
        const Icon = feature.icon;
        const isEven = idx % 2 === 0;
        return (
          <div 
            key={idx}
            className={`section-padding ${idx % 2 !== 0 ? 'bg-muted/20' : ''}`}
          >
            <div className="container-main">
              <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${!isEven ? 'lg:[direction:rtl]' : ''}`}>
                <div className={`space-y-6 animate-fade-up ${!isEven ? 'lg:[direction:ltr]' : ''}`}>
                  <div className="inline-flex items-center gap-3">
                    <div className="p-3 rounded-xl" style={{ background: `${project.color}15` }}>
                      <Icon className="h-6 w-6" style={{ color: project.color }} />
                    </div>
                    <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold tracking-tight">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-base md:text-lg">
                    {feature.description}
                  </p>
                </div>

                <div className={`animate-fade-up ${!isEven ? 'lg:[direction:ltr]' : ''}`} style={{ animationDelay: '0.15s' }}>
                  {feature.before ? (
                    <BeforeAfter before={feature.before} after={feature.after} />
                  ) : feature.screenshot ? (
                    <div className="relative group">
                      <div 
                        className="absolute -inset-3 rounded-3xl opacity-20 blur-2xl transition-opacity group-hover:opacity-40"
                        style={{ background: project.color }}
                      />
                      <img
                        src={feature.screenshot}
                        alt={feature.title}
                        className="relative w-full rounded-2xl border-2 border-border/50 shadow-xl"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden">
                        <ScreenshotPlaceholder label={`Screenshot – ${feature.title}`} />
                      </div>
                    </div>
                  ) : (
                    <ScreenshotPlaceholder label={`Screenshot – ${feature.title}`} />
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
}

export default function RealisationDetailPage() {
  const { projectId } = useParams();
  const project = projects.find(p => p.id === projectId);

  if (!project) {
    return (
      <main className="pt-32 pb-20">
        <div className="container-main text-center">
          <h1 className="text-4xl font-bold mb-4">Projet introuvable</h1>
          <p className="text-muted-foreground mb-8">Ce projet n'existe pas ou a été retiré.</p>
          <Link to="/realisations">
            <Button className="btn-primary">Retour aux réalisations</Button>
          </Link>
        </div>
      </main>
    );
  }

  const isMobileApp = project.category === 'app-mobile';

  return (
    <main data-testid={`project-detail-${project.id}`} className="pb-0">
      <SEO
        title={`${project.name} – Réalisation Axeris Labs`}
        description={project.shortDescription}
        path={`/realisations/${project.id}`}
        noIndex={true}
      />

      {/* Hero - different layout for mobile apps */}
      {isMobileApp ? <MobileAppHero project={project} /> : <WebsiteHero project={project} />}

      {/* Features bar */}
      {project.features && project.features.length > 0 && (
        <section className="py-12 border-y border-border/50 bg-muted/20">
          <div className="container-main">
            <div className="flex flex-wrap items-center justify-center gap-3" data-testid="features-bar">
              <span className="text-sm font-semibold text-muted-foreground mr-2">Fonctionnalités :</span>
              {project.features.map((feat) => (
                <span
                  key={feat.title}
                  className="px-4 py-2 rounded-full text-sm font-semibold bg-background border border-border shadow-sm"
                >
                  {feat.title}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Challenges */}
      {project.challenges && project.challenges.length > 0 && (
        <section className="section-padding" data-testid="challenges-section">
          <div className="container-main">
            <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-up">
              <span className="text-sm uppercase tracking-widest font-bold text-primary mb-3 block">Le défi</span>
              <h2 className="text-3xl md:text-4xl font-bold">
                Les enjeux du projet
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {project.challenges.map((challenge, idx) => (
                <div 
                  key={idx} 
                  className="flex gap-4 p-5 rounded-xl border border-border/50 bg-card animate-fade-up"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <CheckCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground leading-relaxed">{challenge}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features - different layout for mobile apps */}
      {isMobileApp ? <MobileAppFeatures project={project} /> : <WebsiteFeatures project={project} />}

      {/* CTA */}
      <section className="bg-muted/30 section-padding">
        <div className="container-main text-center">
          <div className="max-w-2xl mx-auto animate-fade-up">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Un projet en tête ?
            </h2>
            <p className="text-base md:text-lg text-muted-foreground mb-8">
              Discutons de vos besoins et construisons quelque chose de remarquable ensemble.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/realisations">
                <Button variant="outline" className="rounded-full px-8 py-4 font-bold" data-testid="back-to-all">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Toutes les réalisations
                </Button>
              </Link>
              <a href="/tarifs#contact">
                <Button className="btn-primary" data-testid="cta-contact">
                  Parlons de votre projet
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
